import { useQuery } from '@tanstack/react-query';
import { orderBy, partition } from 'lodash';
import { ListVariableDefinition, ListVariableSpec, VariableOption, VariableStateMap } from '../../../core';
import { usePlugin, useTemplateVariableValues, useTimeRange } from '../../runtime';
import { usePluginQueryContext } from '../../runtime/common';
import { processPluginDefinition } from '../../runtime/data-querying';

/**
 * Extracts the first number from a string.
 * Returns null if no number is found or the value is null/undefined.
 */
function extractNumber(value: string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  const match = value.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}

/**
 * Sorts variable options based on the specified sort type.
 * Invalid values (null/undefined/empty for alphabetical, non-numeric for numerical) are placed at the end.
 */
export function sortVariableOptions(options: VariableOption[], sort: ListVariableSpec['sort']): VariableOption[] {
  if (!sort) {
    return options;
  }

  switch (sort) {
    case 'AlphabeticalAsc':
    case 'AlphabeticalDesc': {
      // Partition into [truthy values, falsy values]. Sort valid strings, put nulls/empty at end.
      const [valid, invalid] = partition(options, (o) => !!o.value);
      const direction = sort === 'AlphabeticalAsc' ? 'asc' : 'desc';
      return [...orderBy(valid, ['value'], [direction]), ...invalid];
    }
    case 'NumericalAsc':
    case 'NumericalDesc': {
      // Partition into [numeric, non-numeric]. Sort numbers, put non-numeric at end.
      const [valid, invalid] = partition(options, (o) => extractNumber(o.value) !== null);
      const direction = sort === 'NumericalAsc' ? 'asc' : 'desc';
      return [...orderBy(valid, [(o) => extractNumber(o.value)], [direction]), ...invalid];
    }
    default:
      return [...options];
  }
}

export function filterVariableList(data: VariableOption[], capturedRegexp: RegExp): VariableOption[] {
  const result: VariableOption[] = [];
  const filteredSet = new Set<string>();
  for (const variableValue of data) {
    if (variableValue.value == null) {
      continue;
    }
    const matches = variableValue.value.matchAll(capturedRegexp);
    let concat = '';
    for (const match of matches) {
      for (let i = 1; i < match.length; i++) {
        const m = match[i];
        if (m !== undefined) {
          concat = `${concat}${m}`;
        }
      }
    }
    if (concat !== '' && !filteredSet.has(concat)) {
      // like that we are avoiding to have duplicating variable value
      filteredSet.add(concat);

      let labelValue = variableValue.label;
      // If the label is the same as the value, we can use the concatenated value as the label
      if (variableValue.label === variableValue.value) {
        labelValue = concat;
      }
      result.push({ label: labelValue, value: concat });
    }
  }

  // If there were no results from regex captures, the user probably entered a plain regex.
  // In this scenario, we use the regex as a filter instead of a capture group:
  if (result.length === 0) {
    data.forEach((d) => {
      if (d.value != null && d.value.match(capturedRegexp)) {
        result.push({ label: d.label, value: d.value });
      }
    });
  }

  return result;
}

export function useDependsOnVariables(definition: ListVariableDefinition) {
  const { data: variablePlugin } = usePlugin('Variable', definition.spec.plugin.kind);
  const variablePluginCtx = usePluginQueryContext();

  const spec = definition.spec.plugin.spec;

  let dependsOnVariables: string[] | undefined;
  if (variablePlugin?.dependsOn) {
    const dependencies = variablePlugin.dependsOn(spec, variablePluginCtx);
    dependsOnVariables = dependencies.variables;
  }

  const variables = useTemplateVariableValues(dependsOnVariables || []);
  const variablesValueKey = getVariableValuesKey(variables);
  return {
    variablesValueKey,
    dependsOnVariables,
    variables,
  };
}

export function useListVariablePluginValues(definition: ListVariableDefinition) {
  const { data: variablePlugin } = usePlugin('Variable', definition.spec.plugin.kind);
  const { refreshKey } = useTimeRange();

  const variablePluginCtx = usePluginQueryContext();
  const { globalFilters } = variablePluginCtx;

  const capturingRegexp =
    definition.spec.capturing_regexp !== undefined ? new RegExp(definition.spec.capturing_regexp, 'g') : undefined;

  const { dependsOnVariables, variablesValueKey, variables } = useDependsOnVariables(definition);

  let waitToLoad = false;
  if (dependsOnVariables) {
    waitToLoad = dependsOnVariables.some((v) => variables[v]?.loading);
  }

  const queryKey = ['ListVariablePluginValues', definition, variablesValueKey, globalFilters];
  if (definition.spec.refresh === 'TimeRangeUpdate') {
    queryKey.push(refreshKey);
  }

  return useQuery(
    queryKey,
    async () => {
      const { definition: modifiedDefinition, notices } = await processPluginDefinition({
        context: variablePluginCtx,
        definition: definition.spec.plugin,
        plugin: variablePlugin!, // we know it's defined since useQuery.options.enabled = true
      });

      if (notices.length > 0) {
        // TODO: We need a better place to show notices for variable plugins
        // eslint-disable-next-line no-console
        console.warn('Notices from variable plugin', notices);
      }

      const resp = await variablePlugin?.getVariableOptions(modifiedDefinition.spec, variablePluginCtx);
      if (resp === undefined) {
        return [];
      }
      const filteredData = capturingRegexp === undefined ? resp.data : filterVariableList(resp.data, capturingRegexp);
      return sortVariableOptions(filteredData, definition.spec.sort);
    },
    { enabled: !!variablePlugin || waitToLoad, cacheTime: 0 }
  );
}

/**
 * Returns a serialized string of the current state of variable values.
 */
export function getVariableValuesKey(v: VariableStateMap) {
  return Object.values(v)
    .map((v) => JSON.stringify(v.value))
    .join(',');
}

export const VARIABLE_TYPES = [
  { label: 'List', kind: 'ListVariable' },
  { label: 'Text', kind: 'TextVariable' },
  { label: 'Interval', kind: 'IntervalVariable' },
] as const;
